import type { Dispatch, SetStateAction } from 'react';

/** Parsed article content_json structure */
export interface ArticleContent {
  h1_title?: string;
  direct_answer_lead?: string;
  key_takeaways?: string[];
  sections?: ArticleSection[];
  faq?: { heading?: string; questions?: { question: string; answer: string }[] };
  conclusion?: { heading?: string; content?: string };
  author_bio?: { author_name?: string; credentials?: string; date?: string };
}

export interface ArticleSection {
  heading?: string;
  content?: string;
  steps?: { step_heading?: string; content?: string }[];
}

/** Render markdown text with proper line-by-line processing */
function renderMarkdown(text: string): string {
  if (!text) return '';
  return text
    .split('\n')
    .map(line => {
      if (line.match(/^###\s+/)) return `<h3 style="color:#111827;font-size:16px;margin:16px 0 8px;font-weight:600;">${line.replace(/^###\s+/, '')}</h3>`;
      if (line.match(/^##\s+/)) return `<h2 style="color:#111827;font-size:20px;margin:20px 0 12px;border-bottom:1px solid #e5e7eb;padding-bottom:8px;">${line.replace(/^##\s+/, '')}</h2>`;
      if (line.match(/^\s*[-*]\s+/)) return `<li style="margin-bottom:6px;margin-left:24px;">${line.replace(/^\s*[-*]\s+/, '')}</li>`;
      const processed = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      if (line.trim() === '') return '<br/>';
      return `<p style="margin:0 0 8px;line-height:1.7;">${processed}</p>`;
    })
    .join('');
}

interface ArticleContentTabProps {
  content: ArticleContent;
  articleTitle: string;
  featuredImageUrl: string | null;
  resolveImageUrl: (url: string | null | undefined) => string | null;
  isEditMode: boolean;
  editContent: ArticleContent | null;
  setEditContent: Dispatch<SetStateAction<ArticleContent | null>>;
}

export function ArticleContentTab({
  content,
  articleTitle,
  featuredImageUrl,
  resolveImageUrl,
  isEditMode,
  editContent,
  setEditContent,
}: ArticleContentTabProps) {
  return (
    <div className="bg-white rounded-[10px] p-12 border border-gray-200 text-gray-800">
      {/* H1 Title */}
      {isEditMode ? (
        <input
          type="text"
          value={editContent?.h1_title || ''}
          onChange={(e) => setEditContent(prev => prev ? { ...prev, h1_title: e.target.value } : prev)}
          className="w-full mb-6 text-gray-900 text-[32px] font-bold leading-tight border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border"
        />
      ) : (
        <h1 className="m-0 mb-6 text-gray-900 text-[32px] font-bold leading-tight">
          {content?.h1_title || articleTitle}
        </h1>
      )}

      {/* Featured image */}
      {featuredImageUrl && (
        <img
          src={resolveImageUrl(featuredImageUrl) || ''}
          alt={content?.h1_title || articleTitle}
          className="w-full max-h-[400px] object-cover rounded-lg mb-8"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      )}

      {/* Direct Answer */}
      <div className="bg-gray-100 border border-gray-300 rounded-lg p-5 mb-6 border-l-4 border-l-blue-500">
        {isEditMode ? (
          <textarea
            value={editContent?.direct_answer_lead || ''}
            onChange={(e) => setEditContent(prev => prev ? { ...prev, direct_answer_lead: e.target.value } : prev)}
            className="w-full min-h-[80px] text-gray-700 text-base leading-relaxed italic border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border bg-white"
          />
        ) : (
          <p className="text-gray-700 m-0 text-base leading-relaxed italic">
            {content?.direct_answer_lead}
          </p>
        )}
      </div>

      {/* Key Takeaways */}
      {(isEditMode || (content?.key_takeaways && content.key_takeaways.length > 0)) && (
        <div className="bg-gray-100 rounded-lg p-5 mb-7 border border-gray-300">
          <h3 className="text-blue-600 m-0 mb-3 text-base">Key Takeaways</h3>
          {isEditMode ? (
            <div>
              {editContent?.key_takeaways?.map((t: string, i: number) => (
                <div key={i} className="flex gap-2 mb-2 items-center">
                  <input
                    type="text"
                    value={t}
                    onChange={(e) => {
                      setEditContent(prev => {
                        if (!prev) return prev;
                        const updated = [...(prev.key_takeaways || [])];
                        updated[i] = e.target.value;
                        return { ...prev, key_takeaways: updated };
                      });
                    }}
                    className="flex-1 text-gray-700 border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border"
                  />
                  <button
                    onClick={() => {
                      setEditContent(prev => {
                        if (!prev) return prev;
                        const updated = (prev.key_takeaways || []).filter((_: string, idx: number) => idx !== i);
                        return { ...prev, key_takeaways: updated };
                      });
                    }}
                    className="px-2.5 py-1.5 bg-red-500 text-white border-none rounded cursor-pointer text-xs font-semibold"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                onClick={() => setEditContent(prev => prev ? {
                  ...prev,
                  key_takeaways: [...(prev.key_takeaways || []), ''],
                } : prev)}
                className="px-3 py-2 bg-blue-500 text-white border-none rounded cursor-pointer text-xs font-semibold mt-2"
              >
                + Add Takeaway
              </button>
            </div>
          ) : (
            <ul className="m-0 pl-5 text-gray-700">
              {(content.key_takeaways ?? []).map((t: string, i: number) => (
                <li key={i} className="mb-2 leading-normal">{t}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Sections */}
      {content?.sections && content.sections.map((section, i) => (
        <div key={i} className="mb-7">
          {isEditMode ? (
            <input
              type="text"
              value={editContent?.sections?.[i]?.heading || ''}
              onChange={(e) => {
                setEditContent(prev => {
                  if (!prev) return prev;
                  const updated = [...(prev.sections || [])];
                  updated[i] = { ...updated[i], heading: e.target.value };
                  return { ...prev, sections: updated };
                });
              }}
              className="w-full text-gray-900 text-xl mb-3 border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border font-semibold"
            />
          ) : (
            <h2 className="text-gray-900 text-xl mb-3 border-b border-gray-200 pb-2">
              {section.heading}
            </h2>
          )}
          {section.steps ? (
            <div>
              {section.steps.map((step, j) => (
                <div key={j} className="mb-4">
                  {isEditMode ? (
                    <>
                      <input
                        type="text"
                        value={editContent?.sections?.[i]?.steps?.[j]?.step_heading || ''}
                        onChange={(e) => {
                          setEditContent(prev => {
                            if (!prev) return prev;
                            const updated = [...(prev.sections || [])];
                            const steps = [...(updated[i]?.steps || [])];
                            steps[j] = { ...steps[j], step_heading: e.target.value };
                            updated[i] = { ...updated[i], steps };
                            return { ...prev, sections: updated };
                          });
                        }}
                        className="w-full text-gray-600 text-base mb-2 border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border font-semibold"
                      />
                      <textarea
                        value={editContent?.sections?.[i]?.steps?.[j]?.content || ''}
                        onChange={(e) => {
                          setEditContent(prev => {
                            if (!prev) return prev;
                            const updated = [...(prev.sections || [])];
                            const steps = [...(updated[i]?.steps || [])];
                            steps[j] = { ...steps[j], content: e.target.value };
                            updated[i] = { ...updated[i], steps };
                            return { ...prev, sections: updated };
                          });
                        }}
                        className="w-full min-h-[120px] text-gray-700 leading-relaxed border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border m-0"
                      />
                    </>
                  ) : (
                    <>
                      <h3 className="text-gray-600 text-base mb-1">{step.step_heading as string}</h3>
                      <p className="text-gray-700 leading-relaxed m-0">{step.content as string}</p>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            isEditMode ? (
              <textarea
                value={editContent?.sections?.[i]?.content || ''}
                onChange={(e) => {
                  setEditContent(prev => {
                    if (!prev) return prev;
                    const updated = [...(prev.sections || [])];
                    updated[i] = { ...updated[i], content: e.target.value };
                    return { ...prev, sections: updated };
                  });
                }}
                className="w-full min-h-[200px] text-gray-700 leading-relaxed border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border"
              />
            ) : (
              <div className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
                __html: renderMarkdown((section.content as string) || '')
              }} />
            )
          )}
        </div>
      ))}

      {/* FAQ */}
      {content?.faq && content.faq.questions && (
        <div className="mb-7">
          {isEditMode ? (
            <input
              type="text"
              value={editContent?.faq?.heading || 'Frequently Asked Questions'}
              onChange={(e) => setEditContent(prev => prev ? {
                ...prev,
                faq: { ...prev.faq, heading: e.target.value },
              } : prev)}
              className="w-full text-gray-900 text-xl mb-4 border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border font-semibold"
            />
          ) : (
            <h2 className="text-gray-900 text-xl mb-4 border-b border-gray-200 pb-2">
              {content.faq.heading || 'Frequently Asked Questions'}
            </h2>
          )}
          {(isEditMode ? editContent?.faq?.questions : content.faq.questions)?.map((faq, i) => (
            <div key={i} className={`mb-5 rounded-lg p-4 ${isEditMode ? 'bg-white border-2 border-blue-500' : 'bg-gray-50 border border-gray-200'}`}>
              {isEditMode ? (
                <>
                  <input
                    type="text"
                    value={faq.question}
                    onChange={(e) => {
                      setEditContent(prev => {
                        if (!prev) return prev;
                        const updated = [...(prev.faq?.questions || [])];
                        updated[i] = { ...updated[i], question: e.target.value };
                        return { ...prev, faq: { ...prev.faq, questions: updated } };
                      });
                    }}
                    placeholder="Question"
                    className="w-full text-gray-900 text-[15px] font-semibold border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border mb-2"
                  />
                  <textarea
                    value={faq.answer}
                    onChange={(e) => {
                      setEditContent(prev => {
                        if (!prev) return prev;
                        const updated = [...(prev.faq?.questions || [])];
                        updated[i] = { ...updated[i], answer: e.target.value };
                        return { ...prev, faq: { ...prev.faq, questions: updated } };
                      });
                    }}
                    placeholder="Answer"
                    className="w-full min-h-[80px] text-gray-600 leading-relaxed text-sm border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border"
                  />
                </>
              ) : (
                <>
                  <h4 className="text-gray-900 m-0 mb-2 text-[15px] font-semibold">{faq.question}</h4>
                  <p className="text-gray-600 m-0 leading-relaxed text-sm">{faq.answer}</p>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Conclusion */}
      {content?.conclusion && (
        <div className="mb-7">
          {isEditMode ? (
            <input
              type="text"
              value={editContent?.conclusion?.heading || ''}
              onChange={(e) => setEditContent(prev => prev ? {
                ...prev,
                conclusion: { ...prev.conclusion, heading: e.target.value },
              } : prev)}
              className="w-full text-gray-900 text-xl mb-3 border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border font-semibold"
            />
          ) : (
            <h2 className="text-gray-900 text-xl mb-3 border-b border-gray-200 pb-2">
              {content.conclusion.heading}
            </h2>
          )}
          {isEditMode ? (
            <textarea
              value={editContent?.conclusion?.content || ''}
              onChange={(e) => setEditContent(prev => prev ? {
                ...prev,
                conclusion: { ...prev.conclusion, content: e.target.value },
              } : prev)}
              className="w-full min-h-[120px] text-gray-700 leading-relaxed border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border"
            />
          ) : (
            <p className="text-gray-700 leading-relaxed">{content.conclusion.content}</p>
          )}
        </div>
      )}

      {/* Author Bio */}
      {content?.author_bio && (
        <div className={`rounded-lg p-4 mt-8 ${isEditMode ? 'bg-white border-2 border-blue-500' : 'bg-gray-50 border-t-2 border-t-gray-200'}`}>
          {isEditMode ? (
            <div className="flex flex-col gap-2">
              <input
                type="text"
                value={editContent?.author_bio?.author_name || ''}
                onChange={(e) => setEditContent(prev => prev ? {
                  ...prev,
                  author_bio: { ...prev.author_bio, author_name: e.target.value },
                } : prev)}
                placeholder="Author name"
                className="text-gray-900 border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border font-semibold"
              />
              <input
                type="text"
                value={editContent?.author_bio?.credentials || ''}
                onChange={(e) => setEditContent(prev => prev ? {
                  ...prev,
                  author_bio: { ...prev.author_bio, credentials: e.target.value },
                } : prev)}
                placeholder="Credentials"
                className="text-gray-600 border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border"
              />
              <input
                type="text"
                value={editContent?.author_bio?.date || ''}
                onChange={(e) => setEditContent(prev => prev ? {
                  ...prev,
                  author_bio: { ...prev.author_bio, date: e.target.value },
                } : prev)}
                placeholder="Date"
                className="text-gray-600 border-2 border-blue-500 rounded-md px-3 py-2 font-[inherit] box-border"
              />
            </div>
          ) : (
            <p className="text-gray-600 m-0 text-[13px]">
              <strong className="text-gray-900">{content.author_bio.author_name}</strong>
              {content.author_bio.credentials && ` — ${content.author_bio.credentials}`}
              {content.author_bio.date && ` • ${new Date(content.author_bio.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
